import React, { FC, useState } from 'react';
import classes from './AgentSettingsDialog.module.scss';
import Modal from './Modal';
import Button from './Button';
import WsConnection from '../service/WsConnection';
import { AgentSettingsDTO, DEFAULT_AGENT_SETTINGS } from '@envclient/envcore';
import { useMyPlayer } from '../hooks/useMyPlayer';

/**
 * Per-seat agent config for the current player, editable at any point in the game (toggling
 * "useAgent" dynamically creates or terminates the seat's live Agent instance server-side).
 * Public/simple:
 * one toggle to let an agent play for you, plus whether it auto-submits and after how long. The
 * compute location (browser), agent type (heuristic) and tuning are fixed defaults kept in the DTO
 * but hidden from players; they're preserved on save so nothing is lost.
 * Agent is opt-in only - useAgent defaults to off (DEFAULT_AGENT_SETTINGS) unless the player has
 * previously turned it on for this seat.
 */
const AgentSettingsDialog: FC<Props> = ({ onClose }) => {
  const myPlayer = useMyPlayer();
  const saved = myPlayer?.agentSettings;

  const [settings, setSettings] = useState<AgentSettingsDTO>(() => ({
    ...DEFAULT_AGENT_SETTINGS,
    ...(saved ?? {})
  }));

  const handleSave = () => {
    WsConnection.send({ type: 'UPDATE_GAME', event: { type: 'UPDATE_AGENT_SETTINGS', settings } });
    onClose();
  };

  return (
    <Modal>
      <div className={classes.mainCont}>
        <div className={classes.bg}></div>
        <div className={classes.panel}>
          <div className={classes.header}>
            <div className={classes.title}>Agent settings</div>
          </div>
          <div className={classes.body}>
            <label className={classes.row}>
              <input
                type="checkbox"
                checked={settings.useAgent}
                onChange={(e) => setSettings((s) => ({ ...s, useAgent: e.target.checked }))}
              />
              Let an agent play for me
            </label>

            {settings.useAgent && (
              <>
                <label className={classes.row}>
                  <input
                    type="checkbox"
                    checked={settings.autoSubmit}
                    onChange={(e) => setSettings((s) => ({ ...s, autoSubmit: e.target.checked }))}
                  />
                  Automatic submit
                </label>

                {settings.autoSubmit && (
                  <label className={classes.field}>
                    Auto-submit delay (seconds)
                    <input
                      type="number"
                      min={1}
                      max={60}
                      value={settings.autoSubmitDelay}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        if (!Number.isNaN(n)) setSettings((s) => ({ ...s, autoSubmitDelay: Math.max(1, n) }));
                      }}
                    />
                  </label>
                )}
              </>
            )}
          </div>
          <div className={classes.btnBar}>
            <Button secondary onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

interface Props {
  onClose: () => void;
}
export default AgentSettingsDialog;
